import { SHIFT } from 'models/baselines';
import { Moment } from 'moment';

export enum MODE {
  VIEW = 'view',
  EDIT = 'edit',
  NONE = ''
}

export type RevisionCodeCollection = {
  label: string;
  value: string;
};
export type IncrementalsRevisionsProps = {
  revisionCodeDropdownValue: string;
  revisionCodeDropdownList: RevisionCodeCollection;
};
export type CompanyCollection = {
  label: string;
  value: string;
};
export type IncrementalItemCompanyEditDetailsProps = {
  companyDropdownList: CompanyCollection[];
  companyDropdownValue: string;
  onAddCompanySelectHandler: (periodIndex: number, companyId: string) => void;
};

export type RevisionCode = {
  code: string;
  startDate: Moment;
  endDate: Moment;
};

export type RevisionCodeWeek = {
  startOfWeek: Moment;
  endOfWeek: Moment;
};

// Values for specific day
export type RequiredDay = {
  day: number;
  night: number;
};

// Values for specific day
export type BaselineDay = {
  day: number;
  night: number;
};

export type IncrementalCompanyDay = {
  company: string;
  day: number;
  night: number;
};

export type IncrementalDay = {
  date: Moment;
  companies: IncrementalCompanyDay[];
};

export type IncrementalCompanyDayUserData = {
  company: string;
  day: string; // for user data this is string because the user could enter invalid data such as "A" instead of "5"
  night: string; // for user data this is string because the user could enter invalid data such as "A" instead of "5"
};

// This stores any data the user entered but has not yet submitted
export type IncrementalDayUserData = {
  date: Moment;
  companies: IncrementalCompanyDay[];
};

// This is not a database identifier
// For UI purposes it identifies which week to edit/display
export type WeekIdentifier = {
  startOfWeek: Moment;
  endOfWeek: Moment;
};

export enum STATUS {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  REQUESTED = 'REQUESTED',
  NONE = ''
}

export type RequestedDay = {
  day: number;
  night: number;
};

export type ApprovedDay = {
  day: number;
  night: number;
};

// Through UI user can change just day or night,
// not necessarily both
export type UserDay = {
  // for day and night, these are string to allow free form data entry,
  // but valid to be able submit should be a whole number >=0
  day: string | null;
  night: string | null;
};

export type JustificationReason = {
  id: string;
  description: string;
};

export type Company = {
  company: string; // Example: kalTire
  description: string; // Example: KalTire
  isActive: boolean;
};

// For a work center, company, and day there is incremental data
// Three possibilities:
// requested: in the database there is a request for this day
// approved: in the database this day was approved previously
// userValue: the user entered something for a day
//            (a day value or night value) but has not yet submitted
// For display purposes on the UI, the ui can use all three of these
// to determine what to display
export type CompanyIncrementalDay = {
  company: Company;
  // if there is no request for this company/day requested will be null
  requested: RequestedDay | null;
  // if this company/day was never approved, approved will be null
  approved: ApprovedDay | null;
  // if nothing has been entered in the ui for this company/day userValue will be null
  userValue: UserDay | null;
};

// A specific day within a work center
export type WorkCenterDay = {
  date: Moment;
  required: RequiredDay; // read only:  from api
  baseline: BaselineDay; // read only: from api
  incrementals: CompanyIncrementalDay[]; // read only: from api
};

export type WorkCenterViewModel = {
  workCenter: string; // Example: HIERELEC
  description: string; // Example: KEARL ELECTRICIAN HIEARACHY

  // This is the list of companies associated with the Work Center from the WorkCenter api.
  // It does not contain companies that were added through UI but not persisted to database yet via API.
  companies: Company[];

  // User adds company in ui.
  // The company is immediately available in the UI for all weeks for that work center,
  // however the company is not associate to the work center in the database via api yet.
  // When the user submits request for the work center, the company is associated via WorkCenters API.
  companiesToAssociate: Company[];
};
// all the work centers involved for that week (each week can have a different set of work centers)
export type WorkCenterWeek = {
  workCenter: WorkCenterViewModel;
  justificationReason: JustificationReason | null; // the reason a request is being made
  status: STATUS; // was it involved most recently in reject/approve by approver?
  mode: MODE; // current mode in the ui (example: edit versus collapsed versus expanded)
  days: WorkCenterDay[];
};

export type Week = {
  weekId: WeekIdentifier;
  workCenterWeeks: WorkCenterWeek[];
};

export type IncrementalItemProps = {
  week: Week;
  workCenterWeek: WorkCenterWeek;
  isUserApprover: boolean;
};

export type IncrementalItemDeps = {
  workCenterWeek: WorkCenterWeek;
};

export type CompanyIncrementalProps = {
  workCenterDay: WorkCenterDay;
  shift: SHIFT;
  readOnly: boolean;
};
